export type AuctionStatus = "open" | "closed" | "cancelled";
export type AuctionListView = "open" | "ended";
export type AuctionBidStatus = "pending" | "confirmed" | "winning" | "outbid" | "refund-due" | "refunded" | "cancelled";
export type AuctionSettlementState = "preparing" | "open" | "broadcasting" | "reconciling" | "settled" | "cancelled" | "failed" | "legacy";

export const AUCTION_MIN_START_PRICE_SATS = 1_000;
export const AUCTION_MIN_INCREMENT_SATS = 1;
export const AUCTION_MAX_PRICE_SATS = 2_100_000_000_000_000;
export const AUCTION_PAGE_SIZE = 30;

export interface AuctionBid {
  id: string;
  auctionId: string;
  bidderPaymentAddress: string;
  bidderOrdinalsAddress: string;
  bidderProfileUsername: string | null;
  bidSats: number;
  serviceFeeSats: number;
  totalSats: number;
  paymentTxid: string;
  networkFeeSats: number;
  settlementTxid: string | null;
  status: AuctionBidStatus;
  createdAt: number;
}

export interface AuctionRecord {
  platform?: "stc" | "artefact";
  kind?: "english" | "dutch";
  floorPriceSats?: number | null;
  id: string;
  title: string;
  description: string;
  inscriptionId: string;
  inscriptionNumber: number | null;
  collectionSlug: string;
  collectionName: string;
  sellerPaymentAddress: string;
  sellerOrdinalsAddress: string;
  startPriceSats: number;
  minIncrementSats: number;
  buyNowSats: number | null;
  startsAt: number;
  endsAt: number;
  serviceFeeBps: number;
  minimumServiceFeeSats: number;
  feeAddress: string;
  status: AuctionStatus;
  settlementState: AuctionSettlementState;
  settlementTxid: string | null;
  settlementError: string;
  winningBidId: string | null;
  highBid: AuctionBid | null;
  bidCount: number;
  canManage: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface AuctionPage {
  platform?: "stc" | "artefact";
  auctions: AuctionRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuctionDetail extends AuctionRecord {
  bids: AuctionBid[];
}

export interface AuctionInput {
  platform?: "stc" | "artefact";
  kind?: "english" | "dutch";
  floorPriceSats?: number | null;
  title: string;
  description: string;
  inscriptionId: string;
  startPriceSats: number;
  minIncrementSats: number;
  buyNowSats?: number | null;
  startsAt: number;
  endsAt: number;
}

/** Integer-only, minute-stepped descending price. Never below the artist's floor. */
export function dutchAuctionPriceSats(auction: Pick<AuctionRecord, "startPriceSats" | "startsAt" | "endsAt" | "floorPriceSats">, now: number): number {
  const floor = auction.floorPriceSats;
  if (!Number.isSafeInteger(floor) || floor === null || floor === undefined || floor < 1 || floor > auction.startPriceSats
    || !Number.isSafeInteger(now) || auction.endsAt <= auction.startsAt) throw new Error("Invalid Dutch auction schedule");
  const duration = auction.endsAt - auction.startsAt;
  const elapsed = Math.min(Math.max(0, now - auction.startsAt), auction.endsAt - auction.startsAt);
  // Reach the reserve on the last minute tick before closing, giving buyers
  // a real open interval in which the floor price can be accepted.
  const steps = Math.max(1, Math.ceil(duration / 60_000) - 1);
  const elapsedSteps = Math.min(steps, Math.floor(elapsed / 60_000));
  const reduction = BigInt(auction.startPriceSats - floor) * BigInt(elapsedSteps) / BigInt(steps);
  return auction.startPriceSats - Number(reduction);
}

export interface AuctionBidQuote {
  reservationId: string;
  auctionId: string;
  bidSats: number;
  serviceFeeSats: number;
  sellerReceivesSats: number;
  networkFeeSats: number;
  totalSats: number;
  psbtBase64: string;
  paymentInputIndexes: number[];
  nextMinimumBidSats: number;
  expiresAt: number;
}

export interface AuctionPreparation {
  auctionId: string;
  inscriptionId: string;
  expiresAt: number;
  psbts: Array<{
    key: "listing";
    psbtBase64: string;
    signingAddress: string;
    signingIndexes: number[];
    useTweakedSigner: true;
  }>;
}

export function nextAuctionBidSats(auction: Pick<AuctionRecord, "startPriceSats" | "minIncrementSats" | "highBid">): number {
  return auction.highBid
    ? auction.highBid.bidSats + auction.minIncrementSats
    : auction.startPriceSats;
}
